import { useEffect, useState } from 'react';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { Code } from 'models/common/CommonCode';

/** 생산공정 공통코드 그룹 */
const PRDN_PROC_GROUP_CD = 'PRDN_PROC_CD';

/**
 * 생산공정 표시 순서 — 공통코드(PRDN_PROC_CD)에 등록된 순서를 그대로 쓴다.
 *
 * 화면에 들어오는 값은 공정 **코드가 아니라 명칭**(prdnProcNm)이라 `cmnCdNm → 순번`으로 맞춘다.
 * 같은 명칭이 여러 코드에 걸려 있으면 먼저 나온 쪽(= 공통코드 순서가 앞선 쪽)을 쓴다.
 *
 * ⚠ 조회에 실패하거나 공통코드에 없는 공정은 지도에 담기지 않는다. 정렬하는 쪽에서
 *   "순서를 모르는 공정"으로 보고 맨 뒤로 보내면 된다(utils/sortRows.ts 참고).
 */

/** 화면 여러 곳에서 쓰므로 조회는 한 번만 하고 결과를 공유한다 (useMechUtTypes와 같은 방식) */
let cachedPromise: Promise<Map<string, number>> | null = null;

const fetchPrdnProcOrder = (): Promise<Map<string, number>> => {
  if (!cachedPromise) {
    cachedPromise = getCommonCodeNames(PRDN_PROC_GROUP_CD)
      .then((codes: Code[] | null) => {
        const order = new Map<string, number>();
        (codes ?? []).forEach((c, i) => {
          const nm = c?.cmnCdNm;
          if (nm && !order.has(nm)) order.set(nm, i);
        });
        return order;
      })
      .catch(() => new Map<string, number>());
  }
  return cachedPromise;
};

export const usePrdnProcOrder = (): Map<string, number> => {
  const [order, setOrder] = useState<Map<string, number>>(() => new Map());

  useEffect(() => {
    let alive = true;
    fetchPrdnProcOrder().then((v) => {
      if (alive) setOrder(v);
    });
    return () => {
      alive = false;
    };
  }, []);

  return order;
};
